import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProjectsClient, ProjectListItem } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { Permissions, usePermissions } from '../../contexts/PermissionContext';

export default function GlobalProjectSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canSearch = hasPermission(Permissions.PROJECT_READ);
  const client = useMemo(() => new ProjectsClient(getApiBaseUrl(), authenticatedFetch), []);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setOptions([]);
    setError(false);
    if (!canSearch || input.trim().length < 2) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = window.setTimeout(() => {
      client.getApiProjects(1, 10, input.trim(), false)
        .then(result => { if (active) setOptions(result.items ?? []); })
        .catch(() => { if (active) setError(true); })
        .finally(() => { if (active) setLoading(false); });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [client, input, canSearch]);

  if (!canSearch) return null;

  return <Autocomplete
    sx={{ width: '100%', maxWidth: 720 }}
    size="small"
    options={options}
    value={null}
    inputValue={input}
    open={open && input.trim().length >= 2}
    onOpen={() => setOpen(true)}
    onClose={() => setOpen(false)}
    onInputChange={(_, value, reason) => {
      if (reason === 'input' || reason === 'clear') { setInput(value); setOptions([]); setError(false); }
    }}
    onChange={(_, project) => {
      if (project?.id) { setInput(''); setOptions([]); setOpen(false); navigate(`/projects/${project.id}`); }
    }}
    filterOptions={items => items}
    getOptionLabel={project => project.internalReference ?? ''}
    isOptionEqualToValue={(option, value) => option.id === value.id}
    autoHighlight
    loading={loading}
    loadingText={t('globalSearch.loading')}
    noOptionsText={t(error ? 'globalSearch.error' : 'globalSearch.noResults')}
    clearText={t('globalSearch.clear')}
    openText={t('globalSearch.open')}
    closeText={t('common.close')}
    forcePopupIcon={false}
    slotProps={{ paper: { sx: { mt: 2, border: 1, borderColor: 'divider', borderRadius: 2, boxShadow: 3 } } }}
    ListboxProps={{ sx: {
      p: 2,
      '& .MuiAutocomplete-option': {
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '148px minmax(0, 1fr)' },
        columnGap: 6,
        rowGap: 2,
        px: 5,
        py: 4,
        borderRadius: 1,
        '&.Mui-focused, &[aria-selected="true"]': { bgcolor: 'primary.50' },
      },
    } }}
    renderOption={(props, project) => <Box component="li" {...props} key={project.id}>
      <Typography noWrap title={project.internalReference} sx={{
        fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
        fontSize: 12,
        lineHeight: '20px',
        fontWeight: 600,
        color: 'primary.dark',
        bgcolor: 'grey.50',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 3,
        py: 1,
        justifySelf: 'start',
        maxWidth: '100%',
      }}>{project.internalReference}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap title={[project.cargoDescription, project.vesselName].filter(Boolean).join(' · ')} sx={{ fontSize: 13, lineHeight: '20px', fontWeight: 500 }}>
          {[project.cargoDescription, project.vesselName].filter(Boolean).join(' · ')}
        </Typography>
        {project.externalClientName && <Typography noWrap title={project.externalClientName} sx={{ fontSize: 12, lineHeight: '18px', mt: 1 }} color="text.secondary">{project.externalClientName}</Typography>}
      </Box>
    </Box>}
    renderInput={params => <TextField
      {...params}
      sx={{ mb: 0 }}
      placeholder={t('globalSearch.placeholder')}
      inputProps={{ ...params.inputProps, 'aria-label': t('globalSearch.label'), maxLength: 200 }}
      InputProps={{ ...params.InputProps,
        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
        endAdornment: <>{loading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
      }}
    />}
  />;
}
